import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { BackButton } from '@/components/common/BackButton';
import { ErrorState } from '@/components/common/ErrorState';
import { PersonBadge } from '@/components/common/PersonBadge';
import { PageHeader } from '@/components/layout/PageHeader';
import { useConfirmBeforeLeave } from '@/hooks/useConfirmBeforeLeave';
import { useHistoryBack } from '@/hooks/useHistoryBack';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { STICKY_SIDE_PANEL_CLASS } from '@/styles/stickySidePanel';
import { leaveApplicationDetailQueryOptions } from '../api/leaveApplications.api';
import { LeaveDetailsSection } from '../components/LeaveDetailsSection';
import { LeaveUsageSummary } from '../components/LeaveUsageSummary';
import { ReviewActionSection } from '../components/ReviewActionSection';

const APPLICANT_ROLE_LABEL = 'ZM';

interface LeaveApplicationDetailPageProps {
  applicationId: number;
}

export function LeaveApplicationDetailPage({ applicationId }: LeaveApplicationDetailPageProps) {
  const navigate = useNavigate();
  const goBack = useHistoryBack(() => void navigate({ to: '/hr-form/leave-applications' }));
  useRememberViewedRow('leaveApplications', applicationId);

  const {
    data: application,
    error,
    isFetching,
    refetch,
  } = useQuery(leaveApplicationDetailQueryOptions(applicationId));

  const [hasUnsavedComment, setHasUnsavedComment] = useState(false);
  const leavePageDialog = useConfirmBeforeLeave(hasUnsavedComment);

  const hasLoadFailed = error !== null && application === undefined;

  return (
    <div className="flex flex-col gap-page-gap">
      <PageHeader
        title="Leave Application Details"
        leading={<BackButton onClick={goBack} />}
        breadcrumbAddon={
          <PersonBadge
            roleLabel={APPLICANT_ROLE_LABEL}
            name={application?.userName}
            isLoading={application === undefined && !hasLoadFailed}
          />
        }
      />

      {hasLoadFailed ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load this leave application"
          onRetry={() => void refetch()}
        />
      ) : (
        <div className="flex flex-col gap-4">
          <LeaveUsageSummary key={applicationId} applicationId={applicationId} />
          <div className="grid gap-4 lg:grid-cols-[minmax(0,7fr)_minmax(18rem,3fr)] lg:items-start">
            <LeaveDetailsSection application={application} />
            <div className={STICKY_SIDE_PANEL_CLASS}>
              <ReviewActionSection
                application={application}
                isRefreshing={isFetching}
                onCommentDirtyChange={setHasUnsavedComment}
              />
            </div>
          </div>
        </div>
      )}

      {leavePageDialog}
    </div>
  );
}
